/**
 * Pure helpers for the M7 rail's conversation: which model answered, when a
 * new cluster of messages starts, how a card's answer reads back, which message
 * a file was sent with, and tool labels in sentence case.
 */
import type { ChatMessage, ChatModel } from "@/types/chat.type";
import type { ChatAttachmentItem } from "@/app/routes/dashboard/api/useSessionAttachments";
import {
  parseAskUserQuestions,
  SKIP_QUESTIONS_MESSAGE,
  type AskUserQuestion,
} from "@/app/routes/dashboard/components/askUserQuestion";
import { findPendingInteraction } from "@/app/routes/dashboard/components/agentInteractions";
import { parsePlanMonthRequest } from "@/app/routes/dashboard/components/planMonth";
import { getMessageUsage } from "@/app/routes/dashboard/components/MessageUsageBadge";
import {
  SKIP_VERIFY_MESSAGE,
  type VerifyItem,
} from "@/app/routes/dashboard/components/verifyWithUser";

/* --- the model that answered ---------------------------------------------------- */

const MODEL_WORDS: Record<string, string> = { gpt: "GPT", ai: "AI" };

/** Ids the picker no longer lists, with the name they had when they ran. */
const RETIRED_MODEL_NAMES: Record<string, string> = {
  "gemini-3.1-pro-preview": "Gemini 3.1 Pro",
};

/** "gemini-3.5-flash-lite" → "Gemini 3.5 Flash Lite", for ids the picker no longer lists. */
export function prettifyModelId(id: string): string {
  return id
    .split(/[-_\s]+/)
    .filter(Boolean)
    .map((word) => MODEL_WORDS[word.toLowerCase()] ?? word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
}

/**
 * The name of the model that wrote a reply, from what the backend saved with it.
 * Each reply stores the model that actually ran, so on Auto two replies in one
 * chat can name two different models. That is the truth the rail shows.
 */
export function answeringModel(
  metadata: Record<string, unknown> | null,
  models: ChatModel[],
): string | null {
  const id = getMessageUsage(metadata)?.model;
  if (!id || id === "auto") return null;
  const listed = models.find((model) => model.id === id);
  return listed?.name ?? RETIRED_MODEL_NAMES[id] ?? prettifyModelId(id);
}

/* --- clusters: one time label per run of messages ------------------------------- */

/** A new cluster starts after this long, or on a new day. */
const CLUSTER_GAP_MS = 20 * 60 * 1000;

function dayKey(date: Date): string {
  return `${date.getFullYear()}-${date.getMonth()}-${date.getDate()}`;
}

export function startsCluster(previousIso: string | null, iso: string): boolean {
  if (!previousIso) return true;
  const previous = new Date(previousIso);
  const current = new Date(iso);
  if (Number.isNaN(previous.getTime()) || Number.isNaN(current.getTime())) return false;
  if (dayKey(previous) !== dayKey(current)) return true;
  return current.getTime() - previous.getTime() > CLUSTER_GAP_MS;
}

const timeFormat = new Intl.DateTimeFormat(undefined, { hour: "numeric", minute: "2-digit" });
const weekdayFormat = new Intl.DateTimeFormat(undefined, { weekday: "short" });
const dayFormat = new Intl.DateTimeFormat(undefined, { month: "short", day: "numeric" });
const dayYearFormat = new Intl.DateTimeFormat(undefined, {
  month: "short",
  day: "numeric",
  year: "numeric",
});

function startOfDay(date: Date): number {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate()).getTime();
}

/** "Today 11:26 AM", "Wed 11:26 AM", "Jul 22, 10:06 AM", as Messages labels a cluster. */
export function clusterLabel(iso: string, now: Date = new Date()): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return "";
  const time = timeFormat.format(date);
  const days = Math.round((startOfDay(now) - startOfDay(date)) / 86_400_000);
  if (days <= 0) return `Today ${time}`;
  if (days === 1) return `Yesterday ${time}`;
  if (days < 7) return `${weekdayFormat.format(date)} ${time}`;
  const day =
    date.getFullYear() === now.getFullYear() ? dayFormat.format(date) : dayYearFormat.format(date);
  return `${day}, ${time}`;
}

/** A message's own time, as hovering it shows: "11:27 AM", "Wed 11:27 AM", "Sep 12, 11:27 AM". */
export function messageTime(iso: string, now: Date = new Date()): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return "";
  const time = timeFormat.format(date);
  const days = Math.round((startOfDay(now) - startOfDay(date)) / 86_400_000);
  if (days <= 0) return time;
  if (days < 7) return `${weekdayFormat.format(date)} ${time}`;
  const day =
    date.getFullYear() === now.getFullYear() ? dayFormat.format(date) : dayYearFormat.format(date);
  return `${day}, ${time}`;
}

/** "0:42", "12:05". */
export function formatDuration(seconds: number | null | undefined): string | null {
  if (!seconds || !Number.isFinite(seconds) || seconds <= 0) return null;
  const total = Math.round(seconds);
  return `${Math.floor(total / 60)}:${String(total % 60).padStart(2, "0")}`;
}

/* --- which message a file was sent with -------------------------------------------- */

export type PlacedAttachments = {
  /** Attached since your last message: they wait in the composer until you send. */
  staged: ChatAttachmentItem[];
  /** Everything else, keyed by the message of yours they went out with. */
  byMessage: Map<string, ChatAttachmentItem[]>;
};

/**
 * Which of your messages a file went out with, by that message's position among
 * your messages, recorded when it was sent.
 */
export type AttachmentClaims = Record<string, number>;

/**
 * Files belong to the first message you sent after attaching them, so they sit
 * above that bubble. A file still on this device, or attached since your last
 * message, has not gone anywhere yet: it waits in the composer.
 *
 * A message just sent is timed by this device and the file by the server, and
 * the two clocks disagree; so files sent in this tab are placed by `claims`
 * (order, not time). History loaded from the server is all server time.
 *
 * `claimOffset`: your messages in older pages not loaded (a Workshop thread is
 * read a page at a time); claims count every message, so they are shifted by
 * it, and a claim on a message not loaded places nothing.
 */
export function placeAttachments(
  messages: ChatMessage[],
  attachments: ChatAttachmentItem[],
  claims: AttachmentClaims = {},
  claimOffset = 0,
): PlacedAttachments {
  const yours = messages.filter((m) => m.role === "user");
  const staged: ChatAttachmentItem[] = [];
  const byMessage = new Map<string, ChatAttachmentItem[]>();
  for (const item of attachments) {
    const claimed = claims[item.id];
    if (claimed !== undefined && claimed - claimOffset < 0) continue;
    const owner = item.local
      ? undefined
      : claimed !== undefined
        ? yours[claimed - claimOffset]
        : yours.find((m) => m.createdAt >= item.createdAt);
    if (!owner) {
      staged.push(item);
      continue;
    }
    byMessage.set(owner.id, [...(byMessage.get(owner.id) ?? []), item]);
  }
  return { staged, byMessage };
}

/* --- a card's answer, read back from the message that carried it ------------------- */

/**
 * The words you sent, without the "On slide: …" line the Strategy page puts in
 * front of every message (withStrategySlideContext). Card answers and the send
 * flight compare against what you wrote, not what the page added.
 */
export function withoutSlideContext(content: string): string {
  return content.replace(/^On slide: [^\n]*\n\n/, "");
}

export type CardAnswer = { skipped: true } | { skipped: false; values: string[] };

/**
 * The answers a user message gave to a questions card, or null when the message
 * is not that card's answer. The card sends a self-contained message (it restates
 * each question), so the rail can show the pick on the card and drop the echo.
 */
export function readQuestionAnswer(
  questions: AskUserQuestion[],
  sent: string,
): CardAnswer | null {
  const content = withoutSlideContext(sent);
  if (content.trim() === SKIP_QUESTIONS_MESSAGE) return { skipped: true };
  if (questions.length === 1) {
    const prefix = `${questions[0]!.question}\n→ `;
    if (!content.startsWith(prefix)) return null;
    return { skipped: false, values: [content.slice(prefix.length).trim()] };
  }
  const lines = content.split("\n");
  if (lines[0]?.trim() !== "Answers:") return null;
  const values: string[] = [];
  for (let i = 0; i < questions.length; i++) {
    const header = lines[1 + i * 2]?.trim();
    const answer = lines[2 + i * 2]?.trim();
    if (header !== `${i + 1}. ${questions[i]!.question}` || !answer?.startsWith("→")) return null;
    values.push(answer.slice(1).trim());
  }
  return { skipped: false, values };
}

/* --- tool labels ------------------------------------------------------------------ */

/**
 * Sentence case for the labels made from a tool's camel-case name
 * ("List Chat Documents" → "List chat documents"). Authored labels, which carry
 * names and punctuation ("Took you to Calendar", "Read x.com"), stay as written.
 */
export function sentenceCaseToolLabel(label: string): string {
  const words = label.split(" ");
  const titleCased =
    words.length > 1 && words.every((word) => /^[A-Z][a-z]+$/.test(word));
  if (!titleCased) return label;
  return [words[0], ...words.slice(1).map((word) => word.toLowerCase())].join(" ");
}

/* --- a value check's verdicts, read back the same way --------------------------------- */

export type VerifyRowAnswer =
  | { state: "confirmed" }
  | { state: "corrected"; correction: string }
  | { state: "wrong" };

export type VerifyAnswer = { skipped: true } | { skipped: false; rows: VerifyRowAnswer[] };

/**
 * The verdicts a user message gave to a value check, or null when the message is
 * not that card's answer. Mirrors formatVerifyMessage in verifyWithUser.ts.
 */
export function readVerifyAnswer(items: VerifyItem[], sent: string): VerifyAnswer | null {
  const content = withoutSlideContext(sent);
  if (content.trim() === SKIP_VERIFY_MESSAGE) return { skipped: true };
  const lines = content.split("\n");
  if (lines[0]?.trim() !== "Checked what you found:") return null;
  const rows: VerifyRowAnswer[] = [];
  for (let i = 0; i < items.length; i++) {
    const prefix = `- ${items[i]!.label}: `;
    const line = lines[1 + i];
    if (!line?.startsWith(prefix)) return null;
    const rest = line.slice(prefix.length);
    if (rest.startsWith("confirmed")) rows.push({ state: "confirmed" });
    else if (rest.startsWith("corrected")) {
      const correction = rest
        .replace(/^corrected\s*\S\s*/, "")
        .replace(/\s*\((?:not .*|you found none)\)$/, "");
      rows.push({ state: "corrected", correction });
    } else if (rest.startsWith("wrong")) rows.push({ state: "wrong" });
    else return null;
  }
  return { skipped: false, rows };
}

/* --- what the agent is waiting on you for ----------------------------------------------- */

/**
 * The words on the closed rail when a card is open: "1 question", "3 questions",
 * "Values to check", or "Plan October 2026". Null when the agent is not waiting on you.
 */
export function waitingOn(messages: ChatMessage[]): string | null {
  const pending = findPendingInteraction(messages);
  if (!pending) return null;
  const events = messages.find((m) => m.id === pending.messageId)?.toolEvents ?? [];
  const asked = events.flatMap((event) => parseAskUserQuestions(event) ?? []);
  const plan = events.map(parsePlanMonthRequest).find((request) => request !== null);
  if (plan && asked.length === 0) return `Plan ${plan.monthLabel}`;
  if (asked.length === 1) return "1 question";
  if (asked.length > 1) return `${asked.length} questions`;
  return "Values to check";
}
